import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { getNewsletter, deleteNewsletter } from '../services/newsletters';
import { useToast } from '../hooks/useToast';
import { useConfirm } from '../hooks/useConfirm';
import PageLoading from '../components/PageLoading';
import PageHeader from '../components/PageHeader';
import PageErrorState from '../components/PageErrorState';
import { formatRelativeDate } from '../utils/dateUtils';
import { setTitle } from '../utils/environment';

const POLL_INTERVAL_MS = 2000;
const IN_FLIGHT_STATUSES = new Set(['pending', 'generating']);

const formatWindow = (start, end) => {
  const fmt = (s) => new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}`;
};

const NewsletterDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();

  const [newsletter, setNewsletter] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const data = await getNewsletter(id);
      setNewsletter(data);
      setError(null);
      return { data, terminal: false };
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Newsletter not found.');
        return { data: null, terminal: true };
      }
      // Transient (timeout, 5xx, network): keep polling silently rather than
      // abandoning the user mid-generation on a single failed request.
      return { data: null, terminal: false };
    }
  };

  useEffect(() => {
    setTitle(
      newsletter?.theme_name
        ? `${newsletter.theme_name} · Read It Never`
        : 'Newsletter · Read It Never'
    );
  }, [newsletter?.theme_name]);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      const { data, terminal } = await load();
      if (cancelled) return;

      if (data || terminal) {
        setIsLoading(false);
      }

      if (terminal) return;

      if (!data || IN_FLIGHT_STATUSES.has(data.status)) {
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [id]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(newsletter.markdown || '');
      showSuccess('Copied to clipboard');
    } catch {
      showError('Failed to copy');
    }
  };

  const handleDelete = async () => {
    if (!(await confirm({
      title: 'Delete this newsletter?',
      danger: true,
      confirmLabel: 'Delete',
    }))) return;
    setIsDeleting(true);
    try {
      await deleteNewsletter(id);
      navigate('/newsletters');
    } catch (err) {
      showError(err.response?.data?.detail || err.message || 'Failed to delete');
      setIsDeleting(false);
    }
  };

  if (isLoading) return <PageLoading message="Loading newsletter…" />;

  if (error) {
    return (
      <PageErrorState
        backTo="/newsletters"
        backLabel="Back to newsletters"
        headerTitle="Newsletter"
        icon="😅"
        iconLabel="Error"
        title="Can't load this newsletter"
        description={error}
        action={{ to: '/newsletters', label: 'Back to newsletters' }}
      />
    );
  }

  const isInFlight = IN_FLIGHT_STATUSES.has(newsletter.status);

  return (
    <div className="min-h-screen bg-clay-bg">
      <PageHeader
        emoji="📰"
        title={<span className="truncate">{newsletter.theme_name}</span>}
        subtitle={
          <>
            {formatWindow(newsletter.window_start, newsletter.window_end)}
            <span className="mx-2">·</span>
            {newsletter.language.toUpperCase()}
            <span className="mx-2">·</span>
            {formatRelativeDate(newsletter.created_at)}
          </>
        }
        backTo="/newsletters"
        backLabel="Back to newsletters"
        actions={newsletter.status === 'completed' && (
          <>
            <button
              onClick={() => navigate('/newsletters/new', {
                state: {
                  prefill: {
                    theme_name: newsletter.theme_name,
                    theme_intent: newsletter.theme_intent,
                    language: newsletter.language,
                    window_start: newsletter.window_start,
                    window_end: newsletter.window_end,
                  },
                },
              })}
              className="btn-clay text-sm"
            >
              Regenerate
            </button>
            <button onClick={handleCopy} className="btn-clay text-sm">
              Copy Markdown
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="btn-clay-danger text-sm"
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </button>
          </>
        )}
      />

      {/* Content */}
      <div className="max-w-3xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
        {/* In-flight status */}
        {isInFlight && (
          <div className="card-clay p-6 text-center">
            <div className="spinner-fun-lg mx-auto mb-3"></div>
            <p className="text-secondary-text capitalize">{newsletter.status}…</p>
          </div>
        )}

        {/* Failed status */}
        {newsletter.status === 'failed' && (
          <div className="notice-clay-error">
            <p className="text-base font-semibold mb-1">Generation failed</p>
            {newsletter.failure_message && (
              <p>{newsletter.failure_message}</p>
            )}
            <div className="flex items-center gap-2 mt-4">
              <button
                onClick={() => navigate('/newsletters/new', {
                  state: {
                    prefill: {
                      theme_name: newsletter.theme_name,
                      theme_intent: newsletter.theme_intent,
                      language: newsletter.language,
                      window_start: newsletter.window_start,
                      window_end: newsletter.window_end,
                    },
                  },
                })}
                className="btn-clay text-sm"
              >
                Regenerate
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="btn-clay-danger text-sm"
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        )}

        {/* Completed: markdown body */}
        {newsletter.status === 'completed' && newsletter.markdown && (
          <div className="card-clay p-6 mb-4">
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <ReactMarkdown>{newsletter.markdown}</ReactMarkdown>
            </div>
          </div>
        )}

        {/* Verification summary */}
        {newsletter.status === 'completed' && (
          <details className="card-clay p-4 text-sm text-secondary-text">
            <summary className="cursor-pointer font-medium text-body-text">Verification summary</summary>
            <p className="mt-2">
              {newsletter.removed_citations
                ? `${newsletter.removed_citations} citation${newsletter.removed_citations === 1 ? '' : 's'} removed during verification.`
                : 'All citations verified successfully.'}
            </p>
          </details>
        )}
      </div>
    </div>
  );
};

export default NewsletterDetail;
